import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTheme } from '../../hooks/useTheme';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';

export interface PageHeroProps {
  /** Main heading (e.g. "Colour Library") */
  title: string;
  /** Optional short line below the title */
  subtitle?: string;
  /** Optional eyebrow label above the title (e.g. "Sanzo Wada Inspired") */
  eyebrow?: string;
  /** Optional content below subtitle (e.g. search bar) */
  children?: React.ReactNode;
  /** When true, omit horizontal padding (parent scroll provides 16px inset). */
  embedded?: boolean;
}

/**
 * Page hero matching Colour Library: same size, font weight, and spacing.
 * Use at the top of each main page for consistent hierarchy.
 */
const PageHero: React.FC<PageHeroProps> = ({
  title,
  subtitle,
  eyebrow,
  children,
  embedded = false,
}) => {
  const { colors } = useTheme();
  const { isMobile } = useResponsiveLayout();

  return (
    <View
      style={[
        styles.hero,
        isMobile && styles.heroMobile,
        embedded && styles.heroEmbedded,
        embedded && isMobile && styles.heroEmbeddedMobile,
        { borderBottomColor: colors.border },
      ]}
    >
      {eyebrow && (
        <View style={styles.eyebrowRow}>
          <View
            style={[
              styles.eyebrowPill,
              { backgroundColor: colors.primary + '18' },
            ]}
          >
            <Text style={[styles.eyebrowText, { color: colors.primary }]}>
              {eyebrow}
            </Text>
          </View>
        </View>
      )}

      <Text
        style={[
          styles.heroTitle,
          isMobile && styles.heroTitleMobile,
          { color: colors.text },
        ]}
      >
        {title}
      </Text>

      {subtitle ? (
        <Text style={[styles.heroSubtitle, { color: colors.secondary }]}>
          {subtitle}
        </Text>
      ) : null}

      {children ? <View style={styles.heroChildren}>{children}</View> : null}
    </View>
  );
};

const styles = StyleSheet.create({
  hero: {
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 18,
    borderBottomWidth: 1,
    marginBottom: 16,
  },
  heroMobile: {
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 14,
    marginBottom: 12,
  },
  heroEmbedded: {
    paddingHorizontal: 0,
  },
  heroEmbeddedMobile: {
    paddingTop: 0,
    paddingBottom: 8,
    marginBottom: 8,
  },
  eyebrowRow: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  eyebrowPill: {
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: 100,
  },
  eyebrowText: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
  },
  heroTitle: {
    fontSize: 32,
    fontWeight: '900',
    letterSpacing: -1,
    lineHeight: 38,
    marginBottom: 6,
  },
  heroTitleMobile: {
    fontSize: 26,
    lineHeight: 32,
    letterSpacing: -0.6,
  },
  heroSubtitle: {
    fontSize: 14,
    lineHeight: 21,
    marginBottom: 12,
  },
  heroChildren: {
    marginTop: 2,
  },
});

export default PageHero;
